import { baseApi } from './baseApi'
import { API_ENDPOINTS } from '@/constants'
import type {
  ApiResponse,
  PaginatedResponse,
  BehaviorRecord,
  CreateBehaviorRecordData,
  BehaviorFilters,
  DailyBehaviorSummary,
} from '@/types'

export const behaviorApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ===================================
    // Queries (GET)
    // ===================================

    /**
     * Get all behavior records with filters
     */
    getBehaviorRecords: builder.query<
      PaginatedResponse<BehaviorRecord>,
      BehaviorFilters | void
    >({
      query: (filters) => ({
        url: API_ENDPOINTS.BEHAVIORS.BASE,
        params: filters || {},
      }),
      transformResponse: (response: ApiResponse<PaginatedResponse<BehaviorRecord>>) =>
        response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Behavior' as const, id })),
              { type: 'Behavior', id: 'LIST' },
            ]
          : [{ type: 'Behavior', id: 'LIST' }],
    }),

    /**
     * Get behavior record by ID
     */
    getBehaviorRecordById: builder.query<BehaviorRecord, string>({
      query: (id) => API_ENDPOINTS.BEHAVIORS.DETAIL(id),
      transformResponse: (response: ApiResponse<BehaviorRecord>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'Behavior', id }],
    }),

    /**
     * Get behavior records by cow ID
     */
    getBehaviorRecordsByCow: builder.query<
      PaginatedResponse<BehaviorRecord>,
      { cowId: string; filters?: BehaviorFilters }
    >({
      query: ({ cowId, filters }) => ({
        url: API_ENDPOINTS.BEHAVIORS.BY_COW(cowId),
        params: filters || {},
      }),
      transformResponse: (response: ApiResponse<PaginatedResponse<BehaviorRecord>>) =>
        response.data,
      providesTags: (_result, _error, { cowId }) => [
        { type: 'Behavior', id: `COW_${cowId}` },
      ],
    }),

    /**
     * Get daily behavior summary for a cow
     */
    getDailyBehaviorSummary: builder.query<
      DailyBehaviorSummary,
      { cowId: string; date: string }
    >({
      query: ({ cowId, date }) => API_ENDPOINTS.BEHAVIORS.DAILY_SUMMARY(cowId, date),
      transformResponse: (response: ApiResponse<DailyBehaviorSummary>) => response.data,
      providesTags: (_result, _error, { cowId, date }) => [
        { type: 'BehaviorSummary', id: `${cowId}_${date}` },
      ],
    }),

    /**
     * Get behavior trends for a cow
     */
    getBehaviorTrends: builder.query<
      DailyBehaviorSummary[],
      { cowId: string; startDate?: string; endDate?: string }
    >({
      query: ({ cowId, startDate, endDate }) => ({
        url: API_ENDPOINTS.BEHAVIORS.TRENDS(cowId),
        params: { startDate, endDate },
      }),
      transformResponse: (response: ApiResponse<DailyBehaviorSummary[]>) => response.data,
      providesTags: (_result, _error, { cowId }) => [
        { type: 'BehaviorTrends', id: cowId },
      ],
    }),

    // ===================================
    // Mutations (POST, PUT, DELETE)
    // ===================================

    /**
     * Create a new behavior record
     */
    createBehaviorRecord: builder.mutation<BehaviorRecord, CreateBehaviorRecordData>({
      query: (data) => ({
        url: API_ENDPOINTS.BEHAVIORS.CREATE,
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ApiResponse<BehaviorRecord>) => response.data,
      invalidatesTags: (_result, _error, data) => [
        { type: 'Behavior', id: 'LIST' },
        { type: 'Behavior', id: `COW_${data.cowId}` },
        { type: 'BehaviorSummary', id: `${data.cowId}_${data.recordDate.split('T')[0]}` },
        { type: 'BehaviorTrends', id: data.cowId },
      ],
    }),

    /**
     * Update a behavior record
     */
    updateBehaviorRecord: builder.mutation<
      BehaviorRecord,
      { id: string; data: Partial<CreateBehaviorRecordData> }
    >({
      query: ({ id, data }) => ({
        url: API_ENDPOINTS.BEHAVIORS.UPDATE(id),
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: ApiResponse<BehaviorRecord>) => response.data,
      invalidatesTags: (_result, _error, { id, data }) => [
        { type: 'Behavior', id },
        { type: 'Behavior', id: 'LIST' },
        ...(data.cowId ? [{ type: 'Behavior' as const, id: `COW_${data.cowId}` }] : []),
      ],
    }),

    /**
     * Delete a behavior record
     */
    deleteBehaviorRecord: builder.mutation<void, string>({
      query: (id) => ({
        url: API_ENDPOINTS.BEHAVIORS.DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Behavior', id },
        { type: 'Behavior', id: 'LIST' },
      ],
    }),
  }),
})

export const {
  useGetBehaviorRecordsQuery,
  useGetBehaviorRecordByIdQuery,
  useGetBehaviorRecordsByCowQuery,
  useGetDailyBehaviorSummaryQuery,
  useGetBehaviorTrendsQuery,
  useCreateBehaviorRecordMutation,
  useUpdateBehaviorRecordMutation,
  useDeleteBehaviorRecordMutation,
} = behaviorApi
